import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import State from "../../../../../../context";
import ModalAdjustColumns from "./component/ModalAdjustColumns";
import ModalModelArchive from "./component/ModalModelArchive";
import ModalLevelRelatedProducts from "../../../proposal/component/ModalLevelRelatedProducts";
import ModalModelSettings from "./component/ModalModelSettings";
import ModalRemoveHolding from "./component/ModalRemoveHolding";
import ModalWelcomeModelBuilder from "./component/ModalWelcomeModelBuilder";
import SectionBuilder from "./component/SectionBuilder";
import SectionHome from "./component/SectionHome";

import StyledContainer from "./style";

import { MODEL_INVESTMENT_AMOUNT } from "./constant";

import {
  addModel,
  updateModel,
  deleteModel,
  duplicateModel,
  updateHoldingColumnOrderWealthAdmin,
  updateFavoriteProductsWealthAdmin,
  getModelCalculation,
  addModelByTemplate,
  updateBuilderWelcomeModalVisibilityAdmin,
  getModelsSaved,
  getTemplateList,
  getModelsSelected,
} from "../../request";
import {
  updateBuilderWelcomeModalVisibilityAdvisor,
  updateFavoriteProductsWealthAdvisor,
  updateHoldingColumnOrderWealthAdvisor,
} from "../../../advisor/request";
import { isOrganizationBx } from "../../../../../../utils/helper/specialized";
import { getMassagedListEtf } from "../../../super_admin/helper";

const PageBuilder = ({ viewAdvisor }) => {
  const [state] = useContext(State);

  const [selectedHoldings, setSelectedHoldings] = useState([]);
  const [weightType, setWeightType] = useState("percentage"); // percentage || fixed
  const [modelCalculation, setModelCalculation] = useState();
  const [isModelDirty, setIsModelDirty] = useState(false);

  const activeModelData =
    state.productsList?.find(it => it._id === state.activeModelId) ??
    state.listModels?.find(it => it._id === state.activeModelId) ??
    state.templateList?.find(it => it._id === state.activeModelId);

  const totalWeight = selectedHoldings?.reduce((acc, it) => acc + it.weight, 0);

  useEffect(() => {
    if (
      state.loading ||
      state.showOverlay ||
      state.profileSettings?.notificationWelcome_DontShowAgain
    )
      return;

    state.setKeyValue("openModalWelcomeModelBuilder", true);
  }, [state.showOverlay]);

  useEffect(() => {
    if (state.pageBuilderActiveSection !== "builder") return;

    if (totalWeight === 1) {
      state.setKeyValue("loading", true);

      getModelCalculation({
        holdings: selectedHoldings.map(it => ({
          ticker: it.ticker ?? it.code,
          weight: it.weight,
        })),
        userEmail: state._id,
        code: activeModelData?.Code,
        name: activeModelData?.Name,
      })
        .then(setModelCalculation)
        .catch(state.showError)
        .finally(() => state.setKeyValue("loading", false));
    }
  }, [selectedHoldings]);

  useEffect(() => {
    if (activeModelData?.Structure?.length) {
      setIsModelDirty(false);

      setSelectedHoldings(
        activeModelData.Structure.map(model => ({
          ...state.productsList?.find(it => it.ticker === model.ticker),
          ...model,
        })).filter(it => it._id)
      );
    } else {
      setSelectedHoldings([]);
      setModelCalculation(null);
    }
  }, [activeModelData]);

  useEffect(() => {
    if (state.templateList || !state.organization?.name) return;

    getTemplateList({
      organization: state.organization?.name,
      userEmail: state._id,
    })
      .then(templateList => state.setKeyValue("templateList", templateList))
      .catch(state.showError);
  }, [state.organization]);

  const handleActionArchive = activeModelId => {
    state.setKeyValue("activeModelId", activeModelId);
    state.setKeyValue("openModalModelArchive", true);
  };

  const handleActionDuplicate = activeModelId => {
    state.setKeyValue("activeModelId", activeModelId);
    state.setKeyValue("openModalModelDuplicate", true);
  };

  const handleActionRename = activeModelId => {
    state.setKeyValue("activeModelId", activeModelId);
    state.setKeyValue("startWithTemplate", false);
    state.setKeyValue("openModalModelSettings", true);
  };

  const handleRemoveHolding = () => {
    setSelectedHoldings(lastState =>
      lastState.filter(it => it._id !== state.activeHoldingId)
    );
    state.setKeyValue("openModalRemoveHolding", false);
    state.showSuccess("Holding removed successfully");
  };

  const handleSelectFinancialProduct = productData => {
    setIsModelDirty(true);

    if (selectedHoldings.find(it => it._id === productData._id)) {
      // remove from selected Holdings
      setSelectedHoldings(lastState =>
        lastState.filter(it => it._id !== productData._id)
      );
      state.showSuccess(`${productData.name} removed from the Holdings list.`);
    } else {
      // add to selected Holdings
      setSelectedHoldings(lastState => [
        ...lastState,
        {
          ...productData,
          type: weightType,
          weight: 0,
        },
      ]);
    }
  };

  const onClickAddHoldings = () => {
    state.setKeyValue("openModalRelatedProducts", true);
  };

  const onClickBreadcrumb = () => {
    state.setKeyValue("activeModelId", null);
    state.setKeyValue("pageBuilderActiveSection", "home");
  };

  const onClickColumns = () => {
    state.setKeyValue("openModalAdjustColumns", true);
  };

  const onClickModelName = ({ modelId }) => {
    state.setKeyValue("activeModelId", modelId);
    state.setKeyValue("pageBuilderActiveSection", "builder");
  };

  const onClickRemoveHolding = ({ activeHoldingId }) => {
    state.setKeyValue("activeHoldingId", activeHoldingId);
    state.setKeyValue("openModalRemoveHolding", true);
  };

  const onClickReWeightPortfolio = () => {
    let updatedWeight, lastItemUpdatedWeight;

    if (weightType === "percentage") {
      // percent type allocation
      updatedWeight = Math.round((1 / selectedHoldings.length) * 1000) / 1000;

      lastItemUpdatedWeight =
        updatedWeight + (1 - updatedWeight * selectedHoldings.length);
    } else {
      // dollar type allocation
      updatedWeight = Math.round(
        MODEL_INVESTMENT_AMOUNT / selectedHoldings.length
      );

      lastItemUpdatedWeight = Math.round(
        updatedWeight +
          (MODEL_INVESTMENT_AMOUNT - updatedWeight * selectedHoldings.length)
      );
    }

    setSelectedHoldings(lastState =>
      lastState.map((it, index) => {
        if (index + 1 === selectedHoldings.length) {
          return {
            ...it,
            weight: lastItemUpdatedWeight,
          };
        } else {
          return {
            ...it,
            weight: updatedWeight,
          };
        }
      })
    );
  };

  const onClickSaveModel = async () => {
    if (totalWeight !== 1) {
      state.showWarning("The Model holdings must equal 100%");
      return;
    }

    if (!activeModelData) {
      state.showWarning(`No active data for model id: ${state.activeModelId}`);
      return;
    }

    state.setKeyValue("loading", true);

    const requestPayload = {
      Code: activeModelData.Name,
      Name: activeModelData.Name,
      Structure: selectedHoldings.map(it => ({
        ticker: it.ticker,
        weight: it.weight,
        type: it.type,
      })),
      organization: activeModelData.organization,
    };

    try {
      await updateModel({
        modelId: state.activeModelId,
        requestPayload,
        userEmail: state._id,
      });

      await reFetchProducts();

      state.showSuccess("Model updated successfully");
    } catch (error) {
      state.showError(error);
    } finally {
      state.setKeyValue("loading", false);
    }
  };

  const onClickStartFromScratch = () => {
    state.setKeyValue("activeModelId", null);
    state.setKeyValue("startWithTemplate", false);
    state.setKeyValue("openModalModelSettings", true);
  };

  const onClickStartWithTemplate = () => {
    state.setKeyValue("activeModelId", null);
    state.setKeyValue("startWithTemplate", true);
    state.setKeyValue("openModalModelSettings", true);
  };

  const onToggleWeightType = () => {
    const updatedWeightType =
      weightType === "percentage" ? "fixed" : "percentage";
    setWeightType(updatedWeightType);

    setSelectedHoldings(oldState =>
      oldState.map(it => ({
        ...it,
        type: updatedWeightType,
        weight:
          updatedWeightType === "percentage"
            ? it.weight / MODEL_INVESTMENT_AMOUNT
            : MODEL_INVESTMENT_AMOUNT * it.weight,
      }))
    );
  };

  const reFetchProducts = async () => {
    try {
      const models = await getModelsSaved({
        organization: state.organization?.name,
        userEmail: state._id,
      });

      const productsList = await getModelsSelected({
        organization: state.organization?.name,
        userEmail: state._id,
      });

      state.setKeyValue("listModels", models);
      state.setKeyValue("productsList", getMassagedListEtf(productsList));
    } catch (error) {
      state.showError(error);
    }
  };

  const submitAdjustColumns = requestBody => {
    if (viewAdvisor) {
      updateHoldingColumnOrderWealthAdvisor({
        requestBody,
        userEmail: state._id,
      })
        .then(() => state.setUserData().catch(state.showError))
        .catch(state.showError);

      return;
    }

    updateHoldingColumnOrderWealthAdmin({
      requestBody,
      userEmail: state._id,
    })
      .then(() => state.setUserData().catch(state.showError))
      .catch(state.showError);
  };

  const submitWelcomeModal = ({ dontShowAgain }) => {
    if (dontShowAgain) {
      const request = viewAdvisor
        ? updateBuilderWelcomeModalVisibilityAdvisor
        : updateBuilderWelcomeModalVisibilityAdmin;

      request({ userEmail: state._id }).catch(state.showError);
    }

    state.setKeyValue("openModalWelcomeModelBuilder", false);
  };

  const submitModelArchive = async () => {
    state.setKeyValue("loading", true);

    try {
      await deleteModel({
        modelId: state.activeModelId,
        userEmail: state._id,
      });

      await reFetchProducts();

      state.setKeyValue("pageBuilderActiveSection", "home");
      state.setKeyValue("openModalModelArchive", false);
      state.showSuccess("Model archived successfully");
    } catch (error) {
      state.showError(error);
    } finally {
      state.setKeyValue("loading", false);
    }
  };

  const submitModelDuplicate = async requestPayload => {
    state.setKeyValue("loading", true);

    try {
      const data = await duplicateModel({
        modelId: activeModelData._id,
        requestPayload,
        userEmail: state._id,
      });

      await reFetchProducts();

      state.setKeyValue("activeModelId", data._id);
      state.setKeyValue("openModalModelDuplicate", false);
      state.setKeyValue("pageBuilderActiveSection", "builder");
      state.showSuccess("New model created successfully");
    } catch (error) {
      state.showError(error);
    } finally {
      state.setKeyValue("loading", false);
    }
  };

  const submitModelSettings = async values => {
    state.setKeyValue("loading", true);

    // add model by template
    if (values.templateId) {
      const requestPayload = {
        Name: values.Name,
        PlanningObjective: values.planningObjective,
        organization: isOrganizationBx(state.organization?.name)
          ? [state.organization?.name]
          : [state.organization?.name, "Level"],
      };

      try {
        const data = await addModelByTemplate({
          requestPayload,
          templateId: values.templateId,
          userEmail: state._id,
        });

        const models = await getModelsSaved({
          organization: state.organization?.name,
          userEmail: state._id,
        });

        const productsList = await getModelsSelected({
          organization: state.organization?.name,
          userEmail: state._id,
        });

        state.setKeyValue("listModels", models);
        state.setKeyValue("productsList", getMassagedListEtf(productsList));
        state.setKeyValue("openModalModelSettings", false);
        state.setKeyValue("activeModelId", data._id);
        state.setKeyValue("pageBuilderActiveSection", "builder");
        state.showSuccess("New model created successfully");
      } catch (error) {
        state.showError(error);
      } finally {
        state.setKeyValue("loading", false);
      }

      return;
    }

    // update active model name
    if (activeModelData) {
      const requestPayload = {
        ...activeModelData,
        Name: values.Name,
      };

      try {
        await updateModel({
          modelId: activeModelData._id,
          requestPayload,
          userEmail: state._id,
        });

        const models = getModelsSaved({
          organization: state.organization?.name,
          userEmail: state._id,
        });

        const productsList = await getModelsSelected({
          organization: state.organization?.name,
          userEmail: state._id,
        });

        state.setKeyValue("listModels", models);
        state.setKeyValue("productsList", getMassagedListEtf(productsList));
        state.setKeyValue("openModalModelSettings", false);
        state.showSuccess("Model updated successfully");
      } catch (error) {
        state.showError(error);
      } finally {
        state.setKeyValue("loading", false);
      }
      return;
    }

    // add new model
    const requestPayload = {
      Code: values.Name,
      Name: values.Name,
      AssetManager: isOrganizationBx(state.organization?.name)
        ? "BX Partners"
        : "LevelWealth",
      Structure: [],
      organization: isOrganizationBx(state.organization?.name)
        ? [state.organization?.name]
        : [state.organization?.name, "Level"],
    };

    try {
      const data = await addModel({ userEmail: state._id, requestPayload });

      await reFetchProducts();

      state.setKeyValue("openModalModelSettings", false);
      state.setKeyValue("activeModelId", data._id);
      state.setKeyValue("pageBuilderActiveSection", "builder");
      state.showSuccess("New model created successfully");
    } catch (error) {
      state.showError(error);
    } finally {
      state.setKeyValue("loading", false);
    }
  };

  const submitToggleFavorite = modelId => {
    const requestFeature = viewAdvisor
      ? updateFavoriteProductsWealthAdvisor
      : updateFavoriteProductsWealthAdmin;

    const isProductFavorite = state.profileSettings?.favoriteProducts?.find(
      it => it === modelId
    );

    const requestBody = isProductFavorite
      ? state.profileSettings.favoriteProducts.filter(it => it !== modelId)
      : [...(state.profileSettings?.favoriteProducts ?? []), modelId];

    state.setKeyValue("loading", true);

    requestFeature({
      requestBody,
      userEmail: state._id,
    })
      .then(() =>
        state
          .setUserData()
          .then(() => {
            state.showSuccess("Favorite status updated successfully");
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const updateHoldingWeight = useDebouncedCallback(({ id, value }) => {
    setIsModelDirty(true);

    setSelectedHoldings(lastState =>
      lastState.map(it => {
        if (it._id === id) {
          return {
            ...it,
            weight: value,
          };
        } else {
          return it;
        }
      })
    );
  }, 300);

  return (
    <StyledContainer>
      {state.pageBuilderActiveSection !== "builder" && (
        <SectionHome
          activeTab={state.pageBuilderActiveTab}
          handleActionArchive={handleActionArchive}
          handleActionDuplicate={handleActionDuplicate}
          handleActionRename={handleActionRename}
          listModels={state.listModels
            ?.filter(it => !it.isTemplate)
            ?.map(it => ({
              ...it,
              key: it._id,
            }))}
          listTemplates={state.templateList?.map(it => ({
            ...it,
            key: it._id,
          }))}
          loading={state.loading}
          onClickStartFromScratch={onClickStartFromScratch}
          onClickStartWithTemplate={onClickStartWithTemplate}
          onClickModelName={onClickModelName}
          onTabChange={newActiveTab =>
            state.setKeyValue("pageBuilderActiveTab", newActiveTab)
          }
          organization={state.organization?.name}
          setActiveTab={activeTabName =>
            state.setKeyValue("pageBuilderActiveTab", activeTabName)
          }
          userEmail={state._id}
          viewAdvisor={viewAdvisor}
        />
      )}

      {state.pageBuilderActiveSection === "builder" && (
        <SectionBuilder
          columnPreferences={state.profileSettings?.tableHoldingsColumnsOrder}
          favoriteProducts={state.profileSettings?.favoriteProducts}
          handleActionArchive={handleActionArchive}
          handleActionDuplicate={handleActionDuplicate}
          handleActionRename={handleActionRename}
          isModelDirty={isModelDirty}
          loading={state.loading}
          modelCalculation={modelCalculation}
          modelData={activeModelData}
          onClickAddHoldings={onClickAddHoldings}
          onClickBreadcrumb={onClickBreadcrumb}
          onClickColumns={onClickColumns}
          onClickRemoveHolding={onClickRemoveHolding}
          onClickReWeightPortfolio={onClickReWeightPortfolio}
          onClickSaveModel={onClickSaveModel}
          onToggleWeightType={onToggleWeightType}
          selectedHoldings={selectedHoldings}
          submitToggleFavorite={submitToggleFavorite}
          updateHoldingWeight={updateHoldingWeight}
          userEmail={state._id}
          viewAdvisor={viewAdvisor}
          weightType={weightType}
        />
      )}

      <ModalModelArchive
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalModelArchive", false)}
        onSuccess={submitModelArchive}
        open={state.openModalModelArchive}
      />
      <ModalAdjustColumns
        columnPreferences={state.preferences}
        onCancel={() => state.setKeyValue("openModalAdjustColumns", false)}
        open={state.openModalAdjustColumns}
        onSuccess={submitAdjustColumns}
      />
      <ModalModelSettings
        activeModelData={activeModelData}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalModelDuplicate", false)}
        onSuccess={submitModelDuplicate}
        open={state.openModalModelDuplicate}
        title="Duplicate Model"
      />
      <ModalModelSettings
        activeModelData={activeModelData}
        templateList={state.templateList}
        loading={state.loading}
        onCancel={() => state.setKeyValue("openModalModelSettings", false)}
        onSuccess={submitModelSettings}
        open={state.openModalModelSettings}
        startWithTemplate={state.startWithTemplate}
        title={activeModelData ? "Rename Model" : "New Model"}
      />
      <ModalWelcomeModelBuilder
        onSuccess={submitWelcomeModal}
        open={state.openModalWelcomeModelBuilder}
      />
      <ModalLevelRelatedProducts
        handleActionClick={handleSelectFinancialProduct}
        handleClose={() => state.setKeyValue("openModalRelatedProducts", false)}
        listProductsActive={selectedHoldings?.map(it => it._id)}
        open={state.openModalRelatedProducts}
      />
      <ModalRemoveHolding
        onCancel={() => state.setKeyValue("openModalRemoveHolding", false)}
        onSuccess={handleRemoveHolding}
        open={state.openModalRemoveHolding}
      />
    </StyledContainer>
  );
};

export default PageBuilder;
